import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Button, Tooltip, Modal, Divider, message, Checkbox } from "antd";
import { uniqueId, uniqBy, isEmpty } from "lodash";
import axios from "axios";
import getCloseModalIcon from "../../../img/close-modal-icon";
import AgreementHolderLine from "../AgreementHolderLine/AgreementHolderLine";
import plusIcon from "../../../img/plus.svg";
import { fetchLine } from "../../../store/slices/statements";
import { getAgreementByAgrId } from "../../../lib/routes/accounts";
import SpanNumberFormat from "../../common/SpanNumberFormat";
import { AgreementsSearchModal } from "../../AgreementsSearch";
import {
  MENU_ACCOUNTS,
  MENU_ACCOUNTS_INDIVIDUAL,
  MENU_ACCOUNTS_INDIVIDUAL_WRITE,
  MENU_ACCOUNTS_ORGANIZATION,
  MENU_ACCOUNTS_ORGANIZATION_WRITE,
  MENU_ACCOUNTS_WRITE,
} from "../../../lib/const/permissions";
import { hasAccess } from "../../RolesModule/hasAccess";
import { agreementsSearchActions } from "../../../store/slices/agreementsSearch/agreementsSearch";
import "./StatementLine.less";

function StatementLine(props) {
  const {
    lineHolder,
    isEditable,
    handleUpdateLine,
    taxTypeRus,
    handleSetProcessableStatus,
  } = props;

  const { defaultAgreementId } = useSelector((state) => state.tenants.tenantProperties);

  const dispatch = useDispatch();

  const [line, setLine] = useState(lineHolder);

  const [, setAddedAccounts] = useState([]);
  const [displayArchiveAgreementsAccounts, setDisplayArchiveAgreementsAccounts] = useState([]);
  const [deletedAccountId, setDeletedAccountId] = useState();

  const [searchClientType, setSearchClientType] = useState("ORGANIZATION");

  const hasAccessToOrganization = useMemo(
    () =>
      hasAccess(
        MENU_ACCOUNTS_ORGANIZATION,
        MENU_ACCOUNTS_ORGANIZATION_WRITE,
        MENU_ACCOUNTS,
        MENU_ACCOUNTS_WRITE
      ),
    [hasAccess]
  );

  const hasAccessToIndividual = useMemo(
    () => hasAccess(MENU_ACCOUNTS_INDIVIDUAL, MENU_ACCOUNTS_INDIVIDUAL_WRITE),
    [hasAccess]
  );

  // Кнопка изменения задизейблена, если доступен только один вид клиента
  const allowedClientChange = useMemo(() => {
    if (hasAccessToOrganization && hasAccessToIndividual) {
      return true;
    }
    return false;
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  // Дефолтный тип клиента в поиске ЮЛ/ФЛ
  useEffect(() => {
    if (hasAccessToOrganization) {
      setSearchClientType("ORGANIZATION");
    } else if (hasAccessToIndividual) {
      setSearchClientType("INDIVIDUAL");
    }
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  const toggleSearchClientType = () => {
    if (allowedClientChange) {
      setSearchClientType((prev) => (prev === "INDIVIDUAL" ? "ORGANIZATION" : "INDIVIDUAL"));
      dispatch(agreementsSearchActions.cleanAccounts());
    }
  };

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const onCloseSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  // Очистка списка accountSelect при входе и выходе
  useEffect(() => {
    dispatch(agreementsSearchActions.cleanAccounts());
    return () => dispatch(agreementsSearchActions.cleanAccounts());
  }, []);

  // С бэка всегда приходит порядок следования договоров в разнобой, что не удобно
  const sortAgreementHolders = (updatedLine) => ({
    ...updatedLine,
    accountHolders: updatedLine.accountHolders.map((accountHolder) => ({
      ...accountHolder,
      agreementHolders: [...accountHolder.agreementHolders].sort((a, b) =>
        a.agreement.agreementNumber > b.agreement.agreementNumber ? 1 : -1
      ),
    })),
  });

  const handleUpdateAgreementHolder = (updatedAgreementHolder) => {
    return handleUpdateLine({ line, updatedAgreementHolder }).then((updatedLine) => {
      handleSetProcessableStatus(updatedLine.batchInfo.isProcessable);
      setLine(sortAgreementHolders(updatedLine.lineHolder));
    });
  };

  const uniqueAccountHolders = uniqBy(line.accountHolders, ({ account }) => account.id);

  const addAccountHolder = (accountId) => {
    setAddedAccounts((prev) => {
      const accountIds = uniqBy([...prev, accountId]);
      dispatch(fetchLine({ lineId: line.line.id, addedAccounts: accountIds }))
        .then(({ payload }) => {
          setLine(sortAgreementHolders(payload.lineHolder));
        })
        .catch(() => {
          message.warning("Ошибка загрузки платежа!");
        });
      return accountIds;
    });
  };

  const onSubmitSearchModal = ([accountId]) => {
    onCloseSearchModal();
    addAccountHolder(accountId);
  };

  const deleteAccountHolder = () => {
    const lineForUpdate = {
      ...line,
      accountHolders: line.accountHolders.filter(({ account }) => account?.id !== deletedAccountId),
    };
    handleUpdateLine({ line: lineForUpdate }).then((updatedLine) => {
      setLine(sortAgreementHolders(updatedLine.lineHolder));
      handleSetProcessableStatus(updatedLine.batchInfo.isProcessable);
      setAddedAccounts((prev) => prev.filter((accountId) => accountId !== deletedAccountId));
    });
  };

  const setDefaultLineAgreement = () => {
    axios(getAgreementByAgrId(defaultAgreementId))
      .then(({ data }) => {
        handleUpdateLine({ line: { ...line, accountHolders: [] } })
          .then(({ batchInfo }) => {
            handleSetProcessableStatus(batchInfo.isProcessable);
            addAccountHolder(data.account.id);
          })
          .catch(() => {
            message.warning("Ошибка обновления платежа!");
          });
      })
      .catch(() => {
        message.warning("Аккаунт по умолчанию не найден!");
      });
  };

  const getAgreementAmountLeft = (agreementHolder) => {
    const lineAmountLeft = line?.lineInfo?.lineAmountLeft
      ? Number(line.lineInfo.lineAmountLeft) * 100
      : 0;
    let agreementTotalAmount = agreementHolder.balanceAmount
      ? Number(agreementHolder.balanceAmount) * 100
      : 0;
    agreementHolder.goalHolders
      .filter(({ bill }) => Boolean(bill?.id))
      .forEach(({ goal }) => {
        const amount = goal?.amount ? Number(goal.amount) * 100 : 0;
        agreementTotalAmount += amount;
      });

    return (lineAmountLeft + agreementTotalAmount) / 100;
  };

  const closeDeleteAccountModal = () => setDeletedAccountId();

  const openDeleteAccountModal = (accountId) => setDeletedAccountId(accountId);

  const confirmDeleteAccountModal = () => {
    deleteAccountHolder();
    closeDeleteAccountModal();
  };

  const headTitleStyle = {
    display: "inline-block",
    margin: "10px 30px 15px 10px",
    fontSize: "15px",
    fontWeight: "bold",
    color: "#8a99a8",
  };

  const renderHeader = () => (
    <>
      <Row>
        <Col span={4}>
          <span style={headTitleStyle}>Данные из биллинга</span>
        </Col>
        <Col offset={4} style={{ marginBottom: "-10px", paddingLeft: "3px" }}>
          <span style={headTitleStyle}>Данные из выписки</span>
          {isEditable && (
            <>
              <Button
                className="statement-line__plusBtn"
                icon={
                  <img src={plusIcon} alt="plus" style={{ width: "17px", paddingBottom: "2px" }} />
                }
                type="text"
                htmlType="button"
                onClick={() => setIsSearchModalOpen(true)}
              >
                <span style={{ color: "#0B7", paddingLeft: "7px" }}>Добавить клиента</span>
              </Button>
              <Button
                type="text"
                onClick={() => setDefaultLineAgreement(line)}
                htmlType="button"
                style={{
                  width: "24px",
                  boxShadow: "none",
                  border: 0,
                }}
              >
                <span
                  style={{
                    fontSize: "17px",
                    fontWeight: "bold",
                    color: "rgb(0, 186, 255)",
                    position: "absolute",
                    top: "-1px",
                    left: "5px",
                  }}
                >
                  ?
                </span>
              </Button>
            </>
          )}
        </Col>
      </Row>
      <Divider className="divider_thin" style={{ borderTop: "1px solid rgba(0, 0, 0, 0.03)" }} />
    </>
  );

  if (isEmpty(line)) return null;

  const lineCellStyle = { marginBottom: "20px" };

  return (
    <>
      {renderHeader()}
      <Row className="statement-line" style={{ minHeight: "305px", padding: "10px" }}>
        <Col span={8} style={{ marginTop: "15px" }}>
          <Row style={lineCellStyle}>
            <Col span={8} className="label">
              {`Плательщик / ${taxTypeRus}`}
            </Col>
            <Col span={16}>{line.line.payerId}</Col>
          </Row>
          <Row style={lineCellStyle}>
            <Col span={8} className="label">
              Сумма платежа
            </Col>
            <Col span={16}>{SpanNumberFormat({ amount: line.line.amount })}</Col>
          </Row>
          <Row style={lineCellStyle}>
            <Col span={8} className="label">
              Остаток платежа
            </Col>
            <Col span={16} style={{ color: line.lineInfo.lineAmountLeft > 0 ? "orange" : "" }}>
              {SpanNumberFormat({ amount: line.lineInfo.lineAmountLeft })}
            </Col>
          </Row>
          <Row style={lineCellStyle}>
            <Col span={8} className="label">
              Дата платежа
            </Col>
            <Col span={16}>{line.line.paymentDate}</Col>
          </Row>
          <Row style={lineCellStyle}>
            <Col span={8} className="label">
              Назначение платежа
            </Col>
            <Col span={16}>{line.line.purpose}</Col>
          </Row>
          <Row style={lineCellStyle}>
            <Col span={8} className="label">
              Номер документа
            </Col>
            <Col span={16}>{line.line.documentNumber}</Col>
          </Row>
        </Col>
        <Col span={16}>
          {uniqueAccountHolders.map((accountHolder, accountHolderIdx) => {
            const accountId = accountHolder.account?.id;
            const onChecked = (e) => {
              const { checked } = e.target;
              if (checked) {
                setDisplayArchiveAgreementsAccounts((prev) => [...prev, accountId]);
              } else {
                setDisplayArchiveAgreementsAccounts((prev) =>
                  prev.filter((item) => item !== accountId)
                );
              }
            };
            const isDisplayArchiveAgreements = displayArchiveAgreementsAccounts.includes(accountId);
            return (
              <div key={uniqueId()}>
                {accountHolderIdx > 0 && <Divider style={{ marginTop: "0px" }} />}
                <Row style={{ height: "32px" }}>
                  <Col
                    className="label"
                    span={4}
                    style={{ paddingLeft: "18px", fontWeight: "bold" }}
                  >
                    <span id={`${line.line?.id}.${accountId}`}>Клиент</span>
                  </Col>
                  <Col
                    span={18}
                    style={{ whiteSpace: "wrap", paddingLeft: "6px", fontWeight: "bold" }}
                  >
                    {accountHolder.organization.fullName}
                  </Col>
                  {isEditable && (
                    <Col span={2} style={{ display: "flex", justifyContent: "space-between" }}>
                      <Tooltip title="Договор неопознанных платежей">
                        <span> </span>
                      </Tooltip>
                      <Tooltip title="Удалить">
                        <Button
                          style={{ paddingTop: "6px", marginRight: "1px" }}
                          className="statement-line__closeIcon"
                          htmlType="button"
                          onClick={() => {
                            openDeleteAccountModal(accountHolder.account.id);
                          }}
                        >
                          {getCloseModalIcon("rgba(0, 0, 0, 0.85)", 12)}
                        </Button>
                      </Tooltip>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col offset={4}>
                    <Checkbox
                      style={{ marginBottom: "6px", marginLeft: "7px", color: "#8a99a8" }}
                      checked={isDisplayArchiveAgreements}
                      onChange={onChecked}
                    >
                      <span>Показать договоры в статусе &quot;Удален, архив&quot;</span>
                    </Checkbox>
                  </Col>
                </Row>
                {accountHolder.agreementHolders
                  .filter(({ agreement }) => {
                    if (isDisplayArchiveAgreements) {
                      return true;
                    }
                    return agreement.agrStatusId !== 6;
                  })
                  .map((agreementHolder) => (
                    <AgreementHolderLine
                      key={uniqueId()}
                      agreementHolder={agreementHolder}
                      isEditable={isEditable}
                      handleUpdateAgreementHolder={handleUpdateAgreementHolder}
                      agreementAmountLeft={getAgreementAmountLeft(agreementHolder)}
                    />
                  ))}
              </div>
            );
          })}
        </Col>
      </Row>
      <Divider className="divider_thin" style={{ marginTop: "10px" }} />
      <AgreementsSearchModal
        clientType={searchClientType}
        toggleClientType={toggleSearchClientType}
        visible={isSearchModalOpen}
        onClose={onCloseSearchModal}
        onSubmit={onSubmitSearchModal}
        combineAgreements
      />
      <Modal
        visible={deletedAccountId}
        bodyStyle={{ height: "110px" }}
        width="500px"
        okText="Ок"
        cancelText="Нет"
        okButtonProps={{
          style: {
            width: "108px",
          },
        }}
        cancelButtonProps={{
          style: {
            width: "108px",
          },
        }}
        onOk={confirmDeleteAccountModal}
        onCancel={closeDeleteAccountModal}
      >
        <div style={{ textAlign: "center", marginTop: "32px", fontSize: "16px" }}>
          Подтвердить удаление аккаунта?
        </div>
      </Modal>
    </>
  );
}

export default StatementLine;
